import { useRef, useState } from 'react';
import { Search, Hash, X } from 'lucide-react';

// Search input with a #tag autocomplete dropdown. Suggests existing tags as the
// user types a token starting with "#". Selecting one inserts "#tag " into the query.
export function TagSearchInput({ value, onChange, tags = [], placeholder, testId, inputClassName }) {
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const tokens = value.split(' ');
  const lastToken = tokens[tokens.length - 1] || '';
  const isTag = lastToken.startsWith('#');
  const query = isTag ? lastToken.slice(1).toLowerCase() : '';
  const suggestions = isTag
    ? tags.filter(t => t.toLowerCase().includes(query)).slice(0, 7)
    : [];
  const showDrop = focused && isTag && suggestions.length > 0;

  const pick = (tag) => {
    const nt = [...tokens.slice(0, -1), `#${tag}`];
    onChange(nt.join(' ') + ' ');
    setActiveIdx(0);
    requestAnimationFrame(() => ref.current?.focus());
  };

  const onKeyDown = (e) => {
    if (!showDrop) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => (i + 1) % suggestions.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => (i - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(suggestions[activeIdx]); }
    else if (e.key === 'Escape') { setFocused(false); }
  };

  return (
    <>
      <Search size={14} className="absolute left-3.5 top-[22px] -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
      <input
        ref={ref}
        data-testid={testId}
        value={value}
        onChange={e => { onChange(e.target.value); setActiveIdx(0); }}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 120)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={inputClassName}
        style={{ fontSize: '16px' }}
      />
      {value && (
        <button onMouseDown={e => { e.preventDefault(); onChange(''); requestAnimationFrame(() => ref.current?.focus()); }}
          data-testid="tag-search-clear" className="absolute right-3 top-[22px] -translate-y-1/2 p-1 z-10">
          <X size={14} className="text-muted-foreground" />
        </button>
      )}
      {showDrop && (
        <div data-testid="tag-suggestions"
          className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-border rounded-xl shadow-lg py-1 max-h-56 overflow-auto">
          <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Tags</p>
          {suggestions.map((t, i) => (
            <button key={t} type="button"
              onMouseDown={e => { e.preventDefault(); pick(t); }}
              onMouseEnter={() => setActiveIdx(i)}
              data-testid={`tag-suggestion-${t}`}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${i === activeIdx ? 'bg-secondary' : ''}`}>
              <Hash size={13} className="text-muted-foreground shrink-0" />
              <span className="truncate">{t}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}
